import { Injectable } from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import {
  AgreementForLoanMeta,
  AgreementSigningStatus,
  ApplicationBaseData,
  ApplicationStage,
  ApplicationType,
  AvailableFundsExplanationDialogComponent,
  BrandingService,
  ButtonAppearance,
  CompanyBrandingHelper,
  CustomerLoanStatus,
  EntityData,
  EntityDocumentsService,
  EntityExData,
  EntityHelper,
  EntityRecentDocuments,
  LoanDocumentService,
  LoanFileTemplateData,
  NeedsAttentionModel,
  parseNumber,
  RenewalEligibilityDto,
  SessionStorageKey,
  SessionStorageState,
  SsoAccountCheckoutState,
  SsoAccountData,
  SsoAccountFacade,
  StringHelper,
  TimeHelper,
  UserMessageData,
  UserMessageFacade,
} from "common";
import * as _ from "lodash";
import moment from "moment";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { AnnouncementData } from "../../announcement/announcement.model";
import { AnnouncementService } from "../../announcement/announcement.service";
import { ApplicationFacade } from "../../application/domain/+state/application.facade";
import { BankAccountService } from "../../bank-account/bank-account.service";
import { BankingService } from "../../bank-account/banking.service";
import { NavigationService } from "../../core/navigation/navigation.service";
import { EntityFacade } from "../../entity/domain/+state/entity.facade";
import { LoanFacade } from "../../loan/domain/+state/loan.facade";
import {
  CaseFinancingLoanData,
  LoanData,
  LoanDataSummary,
  LoanFileTemplate,
  ProductCode,
} from "../../loan/domain/models/loan.model";
import { RenewalService } from "../../renewal/renewal.service";
import { NotificationDialogComponent } from "../../shared/notification-dialog/notification-dialog.component";
import { IsApplicationsOfferVisiblePipe } from "../../shared/pipes/is-applications-offer-visible.pipe";
import { LoyaltyRewardFacade } from "../loyalty-reward/domain/+state/loyalty-reward.facade";
import { LoyaltyRewardDialogComponent, LoyaltyRewardDialogData, LoyaltyRewardDialogResult } from "../loyalty-reward/features/loyalty-reward-dialog/loyalty-reward-dialog.component";
import { OfferAcceptedDialogComponent } from "../loyalty-reward/features/offer-accepted-dialog/offer-accepted-dialog.component";
import {
  DashboardContentComponentData,
} from "./dashboard-content.model";
import { DashboardContentStateService } from "./dashboard-content.state.service";

@Injectable()
export class DashboardContentFacade {
  private notificationDialogRef: MatDialogRef<NotificationDialogComponent>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly entityFacade: EntityFacade,
    private readonly loanFacade: LoanFacade,
    private readonly loanDocumentService: LoanDocumentService,
    private readonly applicationFacade: ApplicationFacade,
    private readonly userMessageFacade: UserMessageFacade,
    private readonly announcementService: AnnouncementService,
    private readonly brandingService: BrandingService,
    private readonly dialog: MatDialog,
    private readonly renewalService: RenewalService,
    private readonly bankingService: BankingService,
    private readonly bankAccountService: BankAccountService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly entityDocumentsService: EntityDocumentsService,
    private readonly navigationService: NavigationService,
    private readonly isApplicationsOfferVisiblePipe: IsApplicationsOfferVisiblePipe,
    private readonly ssoAccountFacade: SsoAccountFacade,
    private readonly loyaltyRewardFacade: LoyaltyRewardFacade,
    private readonly stateService: DashboardContentStateService,
  ) {}

  init(): void {
    this.stateService.clearState();
    this.stateService.setData(this.createInitialData());

    this.initRouteData();
    this.initCheckoutState();
    this.initEntity();
    this.initCampaign();
    this.initAnnouncements();
  }

  getData$(): Observable<DashboardContentComponentData> {
    return this.stateService.getData$();
  }

  dismissMessage(data: DashboardContentComponentData, message: UserMessageData): void {
    if (!data?.currentSsoAccount?.id || !data?.entity?.id) {
      return;
    }

    this.userMessageFacade
      .dismissMessage(message)
      .subscribe(() => this.initMessages(data.entity.id));
  }

  filterCFLoans(data: DashboardContentComponentData, input: LoanData): void {
    const sourceData = data || this.createInitialData();
    const activeLoans = sourceData?.activeLoans?.length
      ? sourceData.activeLoans
      : this.filterOpenLoans(sourceData?.loans || [], sourceData?.currentSsoAccount);
    const activeCFLoans = [
      ...(input != null ? sourceData.cfLoans.filter((loan) => loan.id === input.id) : sourceData.cfLoans || []),
    ].sort((left, right) => this.loanFacade.loanSorter(left, right, sourceData?.currentSsoAccount));

    this.setState({ caseFinancingFiltered: true });
    this.setFilteredLoans(sourceData, activeLoans, activeCFLoans);
  }

  closeFilterCFLoans(data: DashboardContentComponentData): void {
    this.filterCFLoans(data, undefined);
    this.setState({ showCaseFinanceFilter: false });
  }

  filterLoans(data: DashboardContentComponentData, input: LoanData): void {
    const sourceData = data || this.createInitialData();
    const activeLoans = [
      ...(input != null
        ? sourceData.loans.filter((loan) => loan.id === input.id)
        : this.filterOpenLoans(sourceData.loans || [], sourceData.currentSsoAccount)),
    ].sort((left, right) => this.loanFacade.loanSorter(left, right, sourceData.currentSsoAccount));
    const activeCFLoans = sourceData?.activeCFLoans?.length
      ? sourceData.activeCFLoans
      : this.filterOpenCfLoans(sourceData?.cfLoans || [], sourceData?.currentSsoAccount);

    this.setState({ accountsFiltered: true });
    this.setFilteredLoans(sourceData, activeLoans, activeCFLoans);
  }

  closeFilterLoans(data: DashboardContentComponentData): void {
    this.filterLoans(data, undefined);
    this.setState({ showAccountsFilter: false });
  }

  setShowCaseFinanceFilter(data: DashboardContentComponentData, showCaseFinanceFilter: boolean): void {
    this.setState({
      ...(data || {}),
      showCaseFinanceFilter,
    });
  }

  setShowAccountsFilter(data: DashboardContentComponentData, showAccountsFilter: boolean): void {
    this.setState({
      ...(data || {}),
      showAccountsFilter,
    });
  }

  link(): void {
    this.router.navigate(["/bank-link-reminder"]);
  }

  fundAnotherCase(): void {
    this.router.navigate(["/new-case"]);
  }

  showAvailableFundsDialog(data: DashboardContentComponentData): void {
    AvailableFundsExplanationDialogComponent.show(
      this.dialog,
      "Your available funds is the lower amount of either a case's credit limit minus the outstanding balance of that case, or the Master Service Agreement limit minus the aggregate outstanding balances of all your cases.",
      data?.firstActiveCFLoan?.loanInfo?.availableFunds,
      data?.firstActiveCFLoan?.loanInfo?.msaCreditLimit,
    );
  }

  openLoyaltyRewardDialog(): void {
    const loanId = this.stateService.getDataSnapshot()?.loyaltyRewardEligibleLoan?.id;
    if (!loanId) {
      return;
    }

    this.loyaltyRewardFacade.getOffer$(loanId)
      .pipe(take(1))
      .subscribe((offer) => {
        if (!offer?.isEligible || offer?.isAccepted) {
          this.setState({ loyaltyRewardEligibleLoan: null });
          return;
        }
        const dialogData: LoyaltyRewardDialogData = { offer };
        const ref = this.dialog.open(LoyaltyRewardDialogComponent, { width: "500px", data: dialogData });
        ref.afterClosed().subscribe((result: LoyaltyRewardDialogResult) => {
          if (result === "accept") {
            this.loyaltyRewardFacade.acceptOffer(loanId).subscribe(() => {
              this.setState({ loyaltyRewardEligibleLoan: null });
              this.dialog.open(OfferAcceptedDialogComponent, { width: "400px" });
              const entityId = this.stateService.getDataSnapshot()?.entity?.id;
              if (entityId) {
                this.getLoansData(entityId);
              }
            });
          }
        });
      });
  }

  private createInitialData(): DashboardContentComponentData {
    return {
      form: this.formBuilder.group({
        search: "",
      }),
      greeting: "",
      loans: [],
      cfLoans: [],
      activeLoans: [],
      activeCFLoans: [],
      allActiveLoans: [],
      firstActiveCFLoan: null,
      closedLoans: [],
      cfLoanTemplates: [],
      currentSsoAccount: null,
      checkoutState: null,
      messages: [],
      announcements: [],
      entity: null,
      applications: [],
      cfApplications: [],
      nonCfApplications: [],
      cfApplicationsActionRequired: false,
      nonCfApplicationsActionRequired: false,
      productCodes: ProductCode,
      banks: [],
      financingActionRequired: false,
      caseFinancingActionRequired: false,
      noLinkedBanks: true,
      showCaseFinanceFilter: false,
      caseFinancingFiltered: false,
      showAccountsFilter: false,
      accountsFiltered: false,
      hasAvailableOffers: false,
      recentActivity: [],
      recentDocuments: [],
      renewalEligibilityMessage: null,
      needsAttentionModel: null,
      companyBranding: null,
      loyaltyRewardEligibleLoan: null,
    };
  }

  private setState(patch: Partial<DashboardContentComponentData>): DashboardContentComponentData {
    const currentData = this.stateService.getDataSnapshot();
    const nextData = {
      ...currentData,
      ...patch,
    };

    this.stateService.setData(nextData);
    return nextData;
  }

  private initRouteData(): void {
    this.route.data.subscribe((routeData) => {
      this.setState({
        currentSsoAccount: routeData.currentAccount,
      });
    });
  }

  private initCheckoutState(): void {
    this.ssoAccountFacade
      .getSsoAccountCheckoutState$()
      .subscribe((checkoutState: SsoAccountCheckoutState) => {
        this.setState({ checkoutState });
      });
  }

  private watchBankLinkingStatus(entityId: number): void {
    this.bankingService.obsInstitutionLinkStatus.subscribe(() => {
      this.initBanks(entityId);
    });
  }

  private initEntity(): void {
    this.entityFacade
      .getEntity$(parseNumber(this.route.snapshot.queryParams.companyId))
      .subscribe((entity: EntityData) => {
        this.setState({ entity });
        this.watchBankLinkingStatus(entity.id);
        this.getLoansData(entity.id);
        this.getApplications(entity.id);
        this.initBanks(entity.id);
        this.initRecentActivities(entity.id);
        this.initRecentDocuments(entity.id);
        this.initMessages(entity.id);
      });
  }

  private initRecentActivities(entityId: number): void {
    this.entityFacade
      .getRecentActivities(entityId)
      .subscribe((recentActivity) => {
        this.setState({ recentActivity });
      });
  }

  private initRecentDocuments(entityId: number): void {
    this.entityDocumentsService
      .getRecentDocuments(entityId)
      .subscribe((recentDocuments: EntityRecentDocuments[]) => {
        this.setState({ recentDocuments });
      });
  }

  private initCampaign(): void {
    const campaign = this.route.snapshot.queryParamMap.get("campaign");
    if (campaign) {
      sessionStorage.setItem("campaign", campaign);
      this.navigationService.removeQueryParamFromMaintenance("campaign");
    }
  }

  private initCfLoanTemplates(cfLoans: CaseFinancingLoanData[]): void {
    if (!cfLoans?.length) {
      this.setState({ cfLoanTemplates: [] });
      return;
    }

    const calls = cfLoans.map((loan) =>
      this.loanDocumentService
        .getLoanFileTemplates(loan.id)
        .pipe(map((templates: LoanFileTemplateData[]) => ({ loanId: loan.id, templates }))),
    );

    forkJoin(calls).subscribe((cfLoanTemplates: LoanFileTemplate[]) => {
      this.setState({ cfLoanTemplates });
    });
  }

  private dataInit(): void {
    this.brandingService.getBranding().subscribe((companyBranding) => {
      const currentData = this.setState({ companyBranding });
      const activeCFLoans = this.filterOpenCfLoans(currentData.cfLoans || [], currentData.currentSsoAccount);
      const activeLoans = this.filterOpenLoans(currentData.loans || [], currentData.currentSsoAccount);
      const needsAttentionModel = this.createNeedsAttentionModel(currentData.entity as EntityExData, companyBranding);

      const nextData = this.setState({
        greeting: `${TimeHelper.getGreetingTime(moment())}, ${currentData.currentSsoAccount?.firstName}!`,
        financingActionRequired: this.hasLoanActionRequired(activeLoans),
        caseFinancingActionRequired: this.hasLoanActionRequired(activeCFLoans),
        cfApplicationsActionRequired: this.hasApplicationActionRequired(currentData.cfApplications),
        nonCfApplicationsActionRequired: this.hasApplicationActionRequired(currentData.nonCfApplications),
        hasAvailableOffers: _.some(
          currentData.applications,
          (application: ApplicationBaseData) => this.isApplicationsOfferVisiblePipe.transform(application),
        ),
        noLinkedBanks: !_.some(currentData.banks, (bank) => bank.isLinked),
        activeLoans,
        activeCFLoans,
        allActiveLoans: activeCFLoans.length > 0 ? [...activeLoans, ...activeCFLoans] : [...activeLoans],
        firstActiveCFLoan: activeCFLoans.length > 0 ? activeCFLoans[0] : null,
        needsAttentionModel,
      });

      this.pendingRenewalAlert(nextData);
    });
  }

  private getLoansData(entityId: number): void {
    combineLatest([
      this.loanFacade.getSummaryByEntity(entityId),
      this.ssoAccountFacade.getCurrentSsoAccount$(),
    ])
      .pipe(switchMap(([summary, ssoAccount]) => this.enrichWithOrphanedLoans(summary, ssoAccount, entityId)))
      .subscribe((summary: LoanDataSummary) => {
        this.setState({
          loans: summary.otherLoans,
          cfLoans: summary.caseFinancingLoans,
          closedLoans: summary.otherLoans.filter(
            (loan) =>
              loan.customerFacingStatus === CustomerLoanStatus.Closed ||
              loan.customerFacingStatus === CustomerLoanStatus.PendingClosure ||
              loan.customerFacingStatus === CustomerLoanStatus.Renewed,
          ),
        });
        this.dataInit();
        this.initCfLoanTemplates(summary.caseFinancingLoans);
        this.initLoyaltyReward();
      });
  }

  private enrichWithOrphanedLoans(
    summary: LoanDataSummary,
    ssoAccount: SsoAccountData,
    entityId: number,
  ): Observable<LoanDataSummary> {
    const allEntityLoans = [...summary.otherLoans, ...summary.caseFinancingLoans];
    const orphanedLoanMetas = this.findOrphanedLoanMetas(allEntityLoans, ssoAccount, entityId);

    if (orphanedLoanMetas.length === 0) {
      return of(summary);
    }

    const orphanedLoans = orphanedLoanMetas.map((meta) => ({
      id: meta.loanId,
      productCode: meta.productCode,
      productName: meta.productName,
      noAutoPayments: false,
      fullAmortizationStartDateUtc: undefined,
      renewalApplicationId: meta.renewalApplication?.applicationId,
    } as LoanData));

    return of({
      caseFinancingLoans: [...summary.caseFinancingLoans],
      otherLoans: [...summary.otherLoans, ...orphanedLoans],
      totalCount: summary.totalCount + orphanedLoans.length,
    });
  }

  private findOrphanedLoanMetas(entityLoans: LoanData[], ssoAccount: SsoAccountData, entityId: number): AgreementForLoanMeta[] {
    if (!ssoAccount?.loans?.length) {
      return [];
    }

    var renewalForLoan = ssoAccount.loans.filter((ssoLoan) =>
      ssoLoan.agreementState?.entityId === entityId &&
      !entityLoans.find((entityLoan) => entityLoan.id === ssoLoan.loanId) &&
      (
        ssoLoan.agreementState?.isLoanVisibleForUser ||
        (
          !ssoLoan.agreementState?.isLoanVisibleForUser &&
          ssoLoan.renewalApplication?.agreementSigningStatus === AgreementSigningStatus.PendingCurrentUser
        )
      ) &&
      ssoLoan.renewalApplication != null &&
      ssoLoan.renewalApplication.loanId === ssoLoan.renewalApplication.renewalForLoanId,
    );

    return renewalForLoan;
  }

  private initBanks(entityId: number): void {
    this.bankAccountService.getBanks(entityId).subscribe((banks) => {
      this.setState({
        banks,
        noLinkedBanks: !_.some(banks, (bank) => bank.isLinked),
      });
    });
  }

  private getApplications(entityId: number): void {
    this.applicationFacade
      .getApplications$(entityId)
      .subscribe((applications: ApplicationBaseData[]) => {
        this.setState({
          applications,
          cfApplications: applications?.filter((application) => application.applicationType === ApplicationType.LCF),
          nonCfApplications: applications?.filter((application) => application.applicationType !== ApplicationType.LCF),
        });
        this.dataInit();
      });
  }

  private initMessages(entityId: number): void {
    this.userMessageFacade
      .getOnScreenMessages([entityId])
      .subscribe((messagesData) => {
        if (!messagesData) {
          return;
        }

        const messages = messagesData
          .filter((item) => item.subject !== "Refer Twitter")
          .sort((left, right) => (right.importance ?? 0) - (left.importance ?? 0));

        this.setState({ messages });
      });

    this.renewalService
      .getEligible()
      .subscribe((eligibility: RenewalEligibilityDto[]) => {
        const pendingRenewal = eligibility?.find((item) => !item.renewalApplicationId);

        this.setState({
          renewalEligibilityMessage: pendingRenewal
            ? {
                icon: "../assets/ideafinancial-renewal.png",
                body: "Your loan is eligible for renewal",
                buttons: [
                  {
                    label: "START RENEWAL",
                    link: "action:optIn",
                    appearance: ButtonAppearance.emphasized,
                  },
                ],
                loanId: pendingRenewal.loanId,
              }
            : null,
        });
      });
  }

  private initAnnouncements(): void {
    this.announcementService
      .query()
      .subscribe((announcements: AnnouncementData[]) => {
        if (!announcements) {
          return;
        }

        this.setState({ announcements });
      });
  }

  private createNeedsAttentionModel(entity: EntityExData, companyBranding: any): NeedsAttentionModel {
    const advisorPhone = CompanyBrandingHelper.getAdvisorPhone(companyBranding, entity?.advisorPhone);

    return {
      isApplicable: entity?.isDrawSuspended,
      messageInput: EntityHelper.getNeedsAttentionMessage({ ...entity, advisorPhone }),
    };
  }

  private initLoyaltyReward(): void {
    const currentData = this.stateService.getDataSnapshot();
    const eligibleLoan = currentData?.activeLoans?.find((l) => l.loyaltyReward?.isEligible);

    this.setState({ loyaltyRewardEligibleLoan: eligibleLoan || null });

    if (!eligibleLoan) {
      return;
    }

    const shownLoanIds = SessionStorageState.get(SessionStorageKey.LoyaltyRewardPopupShownForLoanIds) ?? [];
    if (!shownLoanIds.includes(eligibleLoan.id)) {
      SessionStorageState.set(SessionStorageKey.LoyaltyRewardPopupShownForLoanIds, [...shownLoanIds, eligibleLoan.id]);
      this.openLoyaltyRewardDialog();
    }
  }

  private pendingRenewalAlert(data: DashboardContentComponentData): void {
    if (this.notificationDialogRef) {
      return;
    }

    const pendingRenewals = data.loans.filter(
      (loan) =>
        loan.customerFacingStatus === CustomerLoanStatus.PendingRenewal &&
        (!loan.optInForRenewal || !loan.renewalApplicationId),
    );

    if (!pendingRenewals.length) {
      return;
    }

    const advisorPhone = StringHelper.phoneFormat(
      CompanyBrandingHelper.getAdvisorPhone(data.companyBranding, pendingRenewals[0].advisorPhone),
    );

    let message = `Your ${pendingRenewals[0].productName} with account number ${pendingRenewals[0].loanNumber} is pending renewal. Give us a call at ${advisorPhone} to start your renewal application.`;
    if (pendingRenewals.length > 1) {
      const loansInfo = pendingRenewals
        .map((loan) => `${loan.productName} with account number ${loan.loanNumber}`)
        .join(" and ");
      message = `Your ${loansInfo} are pending renewal. Give us a call at ${advisorPhone} to start your renewal application.`;
    }

    this.notificationDialogRef = this.dialog.open(NotificationDialogComponent, {
      disableClose: false,
      width: "500px",
    });
    this.notificationDialogRef.componentInstance.title = "Pending renewal";
    this.notificationDialogRef.componentInstance.message = message;
  }

  private hasApplicationActionRequired(applications: ApplicationBaseData[]): boolean {
    return _.some(
      applications,
      (application) =>
        application.stage === ApplicationStage.ApplicationIncomplete ||
        application.stage === ApplicationStage.UnderwritingMissingInfo ||
        application.stage === ApplicationStage.UnderwritingCall,
    );
  }

  private hasLoanActionRequired(loans: LoanData[]): boolean {
    return _.some(
      loans,
      (loan) =>
        loan.customerFacingStatus === CustomerLoanStatus.PendingRenewal ||
        loan.customerFacingStatus === CustomerLoanStatus.Pending,
    );
  }

  private setFilteredLoans(
    data: DashboardContentComponentData,
    activeLoans: LoanData[],
    activeCFLoans: CaseFinancingLoanData[],
  ): void {
    this.setState({
      ...(data || {}),
      activeLoans,
      activeCFLoans,
      allActiveLoans: activeCFLoans.length > 0 ? [...activeLoans, ...activeCFLoans] : [...activeLoans],
      firstActiveCFLoan: activeCFLoans.length > 0 ? activeCFLoans[0] : null,
      financingActionRequired: this.hasLoanActionRequired(activeLoans),
      caseFinancingActionRequired: this.hasLoanActionRequired(activeCFLoans),
    });
  }

  private filterOpenLoans(loans: LoanData[], currentSsoAccount: SsoAccountData): LoanData[] {
    return [...loans]
      .filter((loan) => !this.isClosedLoan(loan))
      .sort((left, right) => this.loanFacade.loanSorter(left, right, currentSsoAccount));
  }

  private filterOpenCfLoans(loans: CaseFinancingLoanData[], currentSsoAccount: SsoAccountData): CaseFinancingLoanData[] {
    return [...loans]
      .filter((loan) => !this.isClosedLoan(loan))
      .sort((left, right) => this.loanFacade.loanSorter(left, right, currentSsoAccount));
  }

  private isClosedLoan(loan: LoanData): boolean {
    return (
      loan.customerFacingStatus === CustomerLoanStatus.Closed ||
      loan.customerFacingStatus === CustomerLoanStatus.PendingClosure ||
      loan.customerFacingStatus === CustomerLoanStatus.Renewed
    );
  }
}
